// ===== All Modal Components =====
import React from 'react';
import { Icon } from './icons.jsx';
import { OriginPill, StatusHTTP } from './primitives.jsx';
import { useStore, makeWebhookPayload } from './store.jsx';
import { useCargos, useScope, usePermission, usePermissions } from './auth.jsx';
import { AddrFields } from './cliente-edit.jsx';
import { ClienteAvatar } from './clientes.jsx';
import {
  DDI_OPCOES, ERP_CONFIG, FUNNELS, MAX_ATTACHMENT_SIZE_MB, MIME_BY_EXT, MONTHS,
  STAGES_BY_FUNNEL, STAGES_PROJETOS, STAGE_NAMES, TIPO_CLIENTE,
  TIPO_CLIENTE_BY_ID, TODAY, brl, brlCompact, maskCNPJ, maskCPF, ganhoNoMes, ritmoDoMes,
  DURACOES_TAREFA, duracaoDoTipo,
  validateCNPJ, validateCPF,
} from './data.jsx';

const { useState: useStateMod, useEffect: useEffectMod, useMemo: useMemoMod } = React;

// `className` vai no `.modal`: é como as regras de celular de um modal só (ex.: `prop-modal`) ficam longe dos outros.
function ModalShell({ title, icon, onClose, footer, children, width = 520, className }) {
  useEffectMod(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="modal-back" onClick={onClose}>
      <div className={className ? `modal ${className}` : "modal"} onClick={e => e.stopPropagation()} style={{ maxWidth: width }}>
        <div className="modal-head">
          {icon && <div style={{ width: 32, height: 32, background: "var(--color-accent-soft)", color: "var(--color-accent-deep)", borderRadius: "var(--radius-sm)", display: "grid", placeItems: "center" }}><Icon name={icon} size={16} /></div>}
          <div className="title">{title}</div>
          <button className="icon-btn" onClick={onClose}><Icon name="x" size={16} /></button>
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-foot">{footer}</div>}
      </div>
    </div>
  );
}

function FormField({ label, hint, children, required, style, className }) {
  return (
    <div className={"field" + (className ? " " + className : "")} style={style}>
      <label>{label}{required && <span style={{ color: "var(--color-danger)" }}> *</span>}{hint && <span style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0, color: "var(--color-ink-faint)", marginLeft: 6 }}>· {hint}</span>}</label>
      {children}
    </div>
  );
}

function Input({ icon, ...props }) {
  return (
    <div className="control">
      {icon && <Icon name={icon} size={14} style={{ color: "var(--color-ink-soft)", marginRight: 8 }} />}
      <input {...props} />
    </div>
  );
}

// Telefone com DDI: +55 por padrão, com opção para clientes estrangeiros.
function PhoneInput({ value, onChange, placeholder = "11 99999-9999" }) {
  const m = String(value || "").match(/^\+(\d{1,3})[\s-]*(.*)$/);
  const ddi = m ? m[1] : "55";
  const resto = m ? m[2] : String(value || "");
  const emit = (nextDdi, nextResto) => onChange(`+${nextDdi} ${nextResto}`.trim());
  return (
    <div style={{ display: "flex", gap: 6 }}>
      <div style={{ width: 104, flexShrink: 0 }}>
        <Select value={ddi} onChange={v => emit(v, resto)}
          options={DDI_OPCOES.map(o => ({ value: o.code, label: o.label, sub: o.nome }))} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <Input icon="phone" placeholder={placeholder} value={resto} onChange={e => emit(ddi, e.target.value)} />
      </div>
    </div>
  );
}

// Dropdown customizado (popover flutuante, igual ao autocomplete de menção).
// Mesma API do antigo <select>: options = string[] | {value,label}[]. Sem <select> nativo.
function Select({ value, onChange, options, placeholder, variant = "field", icon, chipLabel, prefix, multi }) {
  const opts = (options || []).map(o => (o && typeof o === "object") ? o : { value: o, label: o });
  const [open, setOpen] = useStateMod(false);
  const [hi, setHi] = useStateMod(0);
  const [pos, setPos] = useStateMod(null);
  const ref = React.useRef(null);
  const isMulti = !!multi;
  const selectedArr = isMulti ? (Array.isArray(value) ? value.map(String) : []) : null;
  const current = isMulti ? null : opts.find(o => String(o.value) === String(value));

  const place = () => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const desired = Math.min(opts.length * 38 + (isMulti ? 46 : 8), 280);
    const spaceBelow = window.innerHeight - r.bottom - 10;
    const spaceAbove = r.top - 10;
    let placement, maxH;
    if (spaceBelow >= desired) { placement = "below"; maxH = desired; }
    else if (spaceAbove >= desired) { placement = "above"; maxH = desired; }
    else if (spaceBelow >= spaceAbove) { placement = "below"; maxH = spaceBelow; }
    else { placement = "above"; maxH = spaceAbove; }
    setPos({
      left: r.left, width: r.width,
      top: placement === "below" ? r.bottom + 4 : r.top - 4,
      placement, maxH: Math.max(120, maxH),
    });
  };

  const openMenu = () => {
    place();
    setHi(Math.max(0, opts.findIndex(o => String(o.value) === String(value))));
    setOpen(true);
  };

  useEffectMod(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target) && !(e.target.closest && e.target.closest(".select-pop"))) setOpen(false); };
    const reflow = () => place();
    document.addEventListener("mousedown", onDoc);
    window.addEventListener("scroll", reflow, true);
    window.addEventListener("resize", reflow);
    return () => { document.removeEventListener("mousedown", onDoc); window.removeEventListener("scroll", reflow, true); window.removeEventListener("resize", reflow); };
  }, [open]);

  const choose = (o) => {
    if (isMulti) {
      const sv = String(o.value);
      const next = selectedArr.includes(sv) ? selectedArr.filter(x => x !== sv) : [...selectedArr, sv];
      onChange(next);
      // mantém aberto para múltipla seleção
    } else {
      onChange(o.value); setOpen(false); ref.current && ref.current.focus();
    }
  };

  const onKeyDown = (e) => {
    if (!open) {
      if (e.key === "Enter" || e.key === "ArrowDown" || e.key === " ") { e.preventDefault(); openMenu(); }
      return;
    }
    if (e.key === "ArrowDown") { e.preventDefault(); setHi(h => (h + 1) % opts.length); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHi(h => (h - 1 + opts.length) % opts.length); }
    else if (e.key === "Enter") { e.preventDefault(); opts[hi] && choose(opts[hi]); }
    else if (e.key === "Escape") { e.preventDefault(); setOpen(false); }
    else if (e.key === "Tab") { setOpen(false); }
  };

  const isChip = variant === "chip";
  const active = isMulti ? selectedArr.length > 0 : (isChip && current && String(current.value) !== String(opts[0] && opts[0].value));
  // rótulo do gatilho em modo múltiplo: nome do filtro + contagem
  const chipText = isMulti
    ? (chipLabel || prefix || "Selecione")
    : (current ? current.label : (chipLabel || placeholder || "Selecione"));

  return (
    <>
      {isChip ? (
        <button type="button" ref={ref} className={`chip-select2 ${active ? "active" : ""} ${open ? "open" : ""}`} onClick={() => open ? setOpen(false) : openMenu()} onKeyDown={onKeyDown}>
          {icon && <Icon name={icon} size={12} className="cs2-ic" />}
          {prefix && <span className="cs2-prefix">{prefix}</span>}
          <span className="cs2-label">{chipText}</span>
          {isMulti && selectedArr.length > 0 && <span className="cs2-count">{selectedArr.length}</span>}
          <Icon name="chevron-down" size={13} className="select-caret" />
        </button>
      ) : (
        <button type="button" ref={ref} className={`control select-trigger ${open ? "open" : ""}`} onClick={() => open ? setOpen(false) : openMenu()} onKeyDown={onKeyDown}>
          <span className={`select-val ${current ? "" : "ph"}`}>{current ? current.label : (placeholder || "Selecione…")}</span>
          <Icon name="chevron-down" size={14} className="select-caret" />
        </button>
      )}
      {open && pos && (
        <div className={`select-pop ${pos.placement} ${isMulti ? "multi" : ""}`} style={{ left: pos.left, top: pos.top, minWidth: pos.width, maxHeight: pos.maxH, transform: pos.placement === "above" ? "translateY(-100%)" : "none" }}>
          {opts.map((o, i) => {
            const sel = isMulti ? selectedArr.includes(String(o.value)) : String(o.value) === String(value);
            return (
              <button type="button" key={String(o.value)} className={`select-opt ${i === hi ? "active" : ""} ${sel ? "selected" : ""}`}
                onMouseEnter={() => setHi(i)} onMouseDown={e => { e.preventDefault(); choose(o); }}>
                {isMulti && <span className={`opt-check ${sel ? "on" : ""}`}>{sel && <Icon name="check" size={11} />}</span>}
                <span className="so-label">{o.label}</span>
                {!isMulti && sel && <Icon name="check" size={14} className="so-check" />}
              </button>
            );
          })}
          {isMulti && (
            <div className="select-multi-foot">
              <button type="button" className="smf-clear" onMouseDown={e => { e.preventDefault(); onChange([]); }} disabled={selectedArr.length === 0}>Limpar</button>
              <button type="button" className="smf-done" onMouseDown={e => { e.preventDefault(); setOpen(false); }}>Pronto</button>
            </div>
          )}
        </div>
      )}
    </>
  );
}

// ===== Typeahead (autocomplete por digitação) =====
function Typeahead({ value, onChange, options, placeholder, icon = "search", onPickMeta }) {
  const [q, setQ] = useStateMod("");
  const [open, setOpen] = useStateMod(false);
  const [hi, setHi] = useStateMod(0);
  const ref = React.useRef(null);
  const selected = options.find(o => o.value === value);

  useEffectMod(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const ql = q.trim().toLowerCase();
  const hits = ql
    ? options.filter(o => o.label.toLowerCase().includes(ql) || (o.sub || "").toLowerCase().includes(ql)).slice(0, 8)
    : options.slice(0, 8);

  const pick = (o) => { onChange(o.value); onPickMeta && onPickMeta(o); setQ(""); setOpen(false); };

  return (
    <div className="ta" ref={ref}>
      {selected ? (
        <div className="ta-selected">
          <Icon name={selected.icon || "check"} size={13} style={{ color: "var(--color-accent)" }} />
          <span className="ta-sel-label">{selected.label}</span>
          {selected.sub && <span className="ta-sel-sub">{selected.sub}</span>}
          <button type="button" className="icon-btn" style={{ width: 24, height: 24, marginLeft: "auto" }} onClick={() => { onChange(""); setOpen(true); }}><Icon name="x" size={12} /></button>
        </div>
      ) : (
        <div className="control" style={{ position: "relative" }}>
          <Icon name={icon} size={14} style={{ color: "var(--color-ink-soft)", marginRight: 8 }} />
          <input
            placeholder={placeholder}
            value={q}
            onChange={e => { setQ(e.target.value); setOpen(true); setHi(0); }}
            onFocus={() => setOpen(true)}
            onKeyDown={e => {
              if (e.key === "ArrowDown") { e.preventDefault(); setHi(h => Math.min(hits.length - 1, h + 1)); }
              else if (e.key === "ArrowUp") { e.preventDefault(); setHi(h => Math.max(0, h - 1)); }
              else if (e.key === "Enter" && hits[hi]) { e.preventDefault(); pick(hits[hi]); }
              else if (e.key === "Escape") setOpen(false);
            }}
            style={{ flex: 1 }}
          />
        </div>
      )}
      {open && !selected && (
        <div className="ta-pop">
          {hits.length === 0 && <div className="ta-empty">Nenhum resultado para "<strong>{q}</strong>"</div>}
          {hits.map((o, i) => (
            <button type="button" key={o.value} className={`ta-opt ${i === hi ? "hi" : ""}`} onMouseEnter={() => setHi(i)} onClick={() => pick(o)}>
              <Icon name={o.icon || "compass"} size={13} className="ic" />
              <span className="lb">{o.label}</span>
              {o.sub && <span className="sb">{o.sub}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ===== Novo Lead (cliente-first: pede o cliente e autopreenche os dados de contato) =====
function NewLeadModal({ onClose, defaultFunil, simRole }) {
  const store = useStore();
  // Quem enxerga a carteira de todos (escopo `team`: ADM de vendas,
  // orcamentista, backoffice, gestor) cadastra negocio PARA um closer e diz quem
  // o trouxe (Kaio, 25/09). Era `simRole === "backoffice"`, simulacao do
  // prototipo: na pratica ninguem escolhia.
  const escopo = useScope();
  const canChooseOwner = simRole === "backoffice" || escopo === "team";
  const ownerOptions = store.users.filter(u => u.ativo !== false);
  const [form, setForm] = useStateMod({
    empresa: "", contato: "", cargo: "", contatoDecisor: false, email: "", telefone: "",
    segmento: "Residencial", valor: "", stage: "OUT_NOVA",
    origem: "humano", ownerId: store.me.id, originadorId: "", proximaAcao: "", calor: "warm",
    funil: defaultFunil || "projetos",
    clienteId: "", especificadorId: "",
    tipoCliente: "projeto_direto",
    parceriaRepasse: false, pctRepasse: 10,
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const [autofilled, setAutofilled] = useStateMod(false);

  // Auto-derive defaults when funil changes
  useEffectMod(() => {
    if (form.funil === "lojistas") {
      set("tipoCliente", "lojista");
      set("especificadorId", "");
      set("parceriaRepasse", false);
    } else if (form.tipoCliente === "lojista") {
      set("tipoCliente", "projeto_direto");
    }
  }, [form.funil]);

  useEffectMod(() => {
    if (form.tipoCliente !== "projeto_canal") set("especificadorId", "");
  }, [form.tipoCliente]);

  // Quando um cliente é escolhido, autopreenche os dados de contato (editáveis).
  const onPickCliente = (clienteId) => {
    set("clienteId", clienteId);
    const c = store.getClienteById(clienteId);
    if (c) {
      setForm(f => ({
        ...f,
        clienteId,
        contato: c.contato || f.contato,
        cargo: c.cargo || f.cargo,
        contatoDecisor: c.contatoDecisor != null ? c.contatoDecisor : f.contatoDecisor,
        email: c.email || f.email,
        telefone: c.telefone || f.telefone,
        empresa: f.empresa || c.nome,
        // se o cliente já tem canal vinculado, herda
        especificadorId: f.tipoCliente === "projeto_canal" ? (c.canalId || f.especificadorId) : f.especificadorId,
      }));
      setAutofilled(true);
    } else {
      setAutofilled(false);
    }
  };

  const stages = STAGES_BY_FUNNEL[form.funil] || STAGES_PROJETOS;
  const tiposPermitidos = TIPO_CLIENTE.filter(t => t.funil === form.funil);
  const clienteOptions = store.clientes
    .filter(c => !c.isCanal)
    .filter(c => form.funil === "lojistas" ? c.tipo === "lojista" : c.tipo !== "lojista")
    .map(c => ({ value: c.id, label: c.nome, sub: c.cidade, icon: "users" }));
  const espOptions = store.especificadores.map(e => ({ value: e.id, label: e.nome, sub: `${e.tipo}${e.parceriaRepasse ? ` · ${e.pctRepasse}%` : ""}`, icon: "compass" }));
  // Decisao B (27/08): a % do repasse vem do cadastro do CANAL, nao do vendedor.
  // Aqui e so exibicao; quem grava e o gatilho public.deriva_pct_repasse (0037).
  const canalRepassePct = store.especificadores.find(e => e.id === form.especificadorId)?.pctRepasse || 0;

  const submit = async () => {
    if (!form.empresa.trim() || !form.contato.trim()) {
      store.toast({ kind: "err", msg: "Nome do negócio e contato são obrigatórios" });
      return;
    }
    if (!form.proximaAcao.trim()) {
      store.toast({ kind: "err", msg: "Próxima ação é obrigatória para criar o negócio" });
      return;
    }
    if (form.tipoCliente === "projeto_canal" && !form.especificadorId) {
      store.toast({ kind: "err", msg: "Para Projeto · Canal, selecione um especificador" });
      return;
    }
    // createLead já mostra o toast (sucesso ou falha) internamente. `await` +
    // checagem do id é só para não fechar o formulário — e perder o que o usuário
    // digitou — quando a gravação é recusada pelo banco.
    const id = await store.createLead({
      ...form, clienteId: form.clienteId || null, especificadorId: form.especificadorId || null,
      // "Quem trouxe" vazio = o responsavel escolhido. So quem tem escopo `team`
      // escolhe; os demais nao mandam, e a origem e quem esta na sessao.
      originadorId: canChooseOwner ? (form.originadorId || form.ownerId) : undefined,
      // A tabela do cliente, fixada no negocio na criacao (0077).
      tabelaId: (form.clienteId && store.getClienteById(form.clienteId)?.tabelaId) || null,
    });
    if (id) onClose();
  };

  const lockedOwner = ownerOptions.find(u => u.id === form.ownerId) || store.me;

  return (
    <ModalShell title="Novo negócio" icon="plus" onClose={onClose} width={680} footer={
      <>
        <button className="btn ghost" onClick={onClose}>Cancelar</button>
        <button className="btn primary" onClick={submit}><Icon name="check" size={13} /> Criar negócio</button>
      </>
    }>
      {/* Funil */}
      <FormField label="Funil" required>
        <div className="funnel-pick">
          {FUNNELS.map(f => (
            <button key={f.id} type="button" className={`funnel-pick-btn ${form.funil === f.id ? "on" : ""}`} onClick={() => set("funil", f.id)}>
              <Icon name={f.icon} size={14} />
              <div>
                <div className="nm">{f.name}</div>
                <div className="sub">{f.cycle}</div>
              </div>
            </button>
          ))}
        </div>
      </FormField>

      {/* 1 — Cliente primeiro */}
      <div className="section-h">1 · Cliente<span>· comece pelo cliente — os dados de contato são preenchidos automaticamente</span></div>
      <FormField label="Cliente" hint="busque pelo nome">
        <Typeahead
          value={form.clienteId}
          onChange={(v) => v ? onPickCliente(v) : (set("clienteId", ""), setAutofilled(false))}
          options={clienteOptions}
          placeholder="Digite para buscar um cliente cadastrado…"
          icon="users"
        />
      </FormField>
      {autofilled && (
        <div className="autofill-note">
          <Icon name="sparkles" size={12} />
          Contato, e-mail e telefone preenchidos a partir do cadastro. Você pode editar abaixo.
        </div>
      )}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <FormField label="Contato" required>
          <Input icon="user" placeholder="Maria da Silva" value={form.contato} onChange={e => set("contato", e.target.value)} />
        </FormField>
        <FormField label="Cargo">
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <Input placeholder="Ex.: Diretor de Compras" value={form.cargo} onChange={e => set("cargo", e.target.value)} />
            <label className={`decisor-toggle ${form.contatoDecisor ? "on" : ""}`}>
              <input type="checkbox" checked={form.contatoDecisor} onChange={e => set("contatoDecisor", e.target.checked)} />
              <Icon name={form.contatoDecisor ? "shield-check" : "shield"} size={13} />
              <span>É o decisor da compra</span>
            </label>
          </div>
        </FormField>
        <FormField label="E-mail">
          <Input icon="mail" type="email" placeholder="contato@email.com.br" value={form.email} onChange={e => set("email", e.target.value)} />
        </FormField>
        <FormField label="Telefone">
          <PhoneInput value={form.telefone} onChange={v => set("telefone", v)} />
        </FormField>
      </div>

      {/* 2 — Negócio */}
      <div className="section-h" style={{ marginTop: 8 }}>2 · Negócio</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <FormField label="Nome do negócio" required>
          <Input icon="tag" placeholder="Ex: Residência Itacolomi" value={form.empresa} onChange={e => set("empresa", e.target.value)} />
        </FormField>
        <FormField label="Segmento">
          <Select value={form.segmento} onChange={v => set("segmento", v)} options={["Residencial", "Hospitalidade", "Foodservice", "Corporativo", "Lojista", "Industrial", "Educacional"]} />
        </FormField>
        <FormField label="Valor estimado">
          <Input icon="wallet" type="number" placeholder="120000" value={form.valor} onChange={e => set("valor", e.target.value)} />
        </FormField>
        <FormField label="Estágio inicial">
          <Select value={form.stage} onChange={v => set("stage", v)} options={stages.filter(s => s.id !== "GANHO").map(s => ({ value: s.id, label: s.name }))} />
        </FormField>
      </div>

      <FormField label="Tipo de cliente" required>
        <div className="tipo-pick">
          {tiposPermitidos.map(t => (
            <button key={t.id} type="button" className={`tipo-pick-btn ${form.tipoCliente === t.id ? "on" : ""}`} style={{ borderColor: form.tipoCliente === t.id ? t.color : undefined, background: form.tipoCliente === t.id ? t.tone : undefined, color: form.tipoCliente === t.id ? t.color : undefined }} onClick={() => set("tipoCliente", t.id)}>
              <span className="dot" style={{ background: t.color }} />
              {t.label}
            </button>
          ))}
        </div>
      </FormField>
      {form.tipoCliente === "projeto_canal" && (
        <FormField label="Especificador / Canal" required hint="digite o nome para buscar">
          <Typeahead
            value={form.especificadorId}
            onChange={(v) => set("especificadorId", v)}
            options={espOptions}
            placeholder="Digite o nome do escritório / canal…"
            icon="compass"
          />
        </FormField>
      )}
      {form.tipoCliente === "projeto_canal" && form.especificadorId && (
        <div className="repasse-block">
          <label className="check-row">
            <input type="checkbox" checked={form.parceriaRepasse} onChange={e => set("parceriaRepasse", e.target.checked)} />
            <span>Parceria com repasse?</span>
          </label>
          {form.parceriaRepasse && (
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div className="mini-mono-input" title="Definido no cadastro do canal">
                <strong>{canalRepassePct}</strong> %
              </div>
              <div style={{ fontSize: 12, color: "var(--color-ink-soft)" }}>
                % do repasse vem do cadastro do canal. Em <strong>Ganho</strong>, o sistema cria um repasse de <strong>{brl(Math.round((Number(form.valor) || 0) * (canalRepassePct / 100)))}</strong> em Contas a Pagar.
              </div>
            </div>
          )}
        </div>
      )}

      {/* 3 — Atribuição */}
      <div className="section-h" style={{ marginTop: 8 }}>3 · Atribuição</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
        <FormField label="Responsável" hint={canChooseOwner ? "o closer que cuida do negócio" : "sempre você"}>
          {canChooseOwner ? (
            <Select value={form.ownerId} onChange={v => set("ownerId", v)} options={ownerOptions.map(u => ({ value: u.id, label: `${u.nome}` }))} />
          ) : (
            <div className="locked-owner">
              <div className="avatar" style={{ width: 22, height: 22, fontSize: 9, background: "var(--color-accent)", color: "white" }}>{lockedOwner.initials}</div>
              <span>{lockedOwner.nome}</span>
              <Icon name="lock" size={12} style={{ marginLeft: "auto", color: "var(--color-ink-faint)" }} />
            </div>
          )}
        </FormField>
        {canChooseOwner && (
          // Vazio = o proprio responsavel trouxe. Quem cadastra (esta sessao)
          // entra na Cadeia como "Cadastrado por", sem tomar o lugar da origem.
          <FormField label="Quem trouxe" hint="fica como origem do negócio">
            <Select value={form.originadorId || form.ownerId} onChange={v => set("originadorId", v)}
              options={ownerOptions.map(u => ({ value: u.id, label: `${u.nome}` }))} />
          </FormField>
        )}
        <FormField label="Calor inicial">
          <Select value={form.calor} onChange={v => set("calor", v)} options={[{value: "cold", label: "Frio"}, {value: "warm", label: "Morno"}, {value: "hot", label: "Quente"}]} />
        </FormField>
        <FormField label="Próxima ação" required>
          <Input icon="clock" placeholder="Ex.: Visita técnica" value={form.proximaAcao} onChange={e => set("proximaAcao", e.target.value)} />
        </FormField>
      </div>
    </ModalShell>
  );
}

// ===== People Picker (responsável + envolvidos mencionados) =====
// Duracao de tarefa: rotulo e opcoes do seletor. A atual entra na lista mesmo
// fora do padrao (tarefa antiga de 45 min nao pode virar 1 h ao abrir).
const horaMin = (d) => { const x = new Date(d); return String(x.getHours()).padStart(2, "0") + ":" + String(x.getMinutes()).padStart(2, "0"); };
const rotuloDuracao = (min) => (min < 60 ? `${min} min` : (min % 60 ? `${Math.floor(min / 60)}h${String(min % 60).padStart(2, "0")}` : `${min / 60} h`));
const opcoesDuracao = (atual) => [...new Set([...DURACOES_TAREFA, atual])].filter(m => m > 0).sort((a, b) => a - b)
  .map(m => ({ value: String(m), label: rotuloDuracao(m) }));

// `janela` ({inicio, fim}) liga a consulta de agenda: quem esta sendo convidado
// AGORA e checado contra a agenda dele naquele horario, e a tela avisa quem vai
// entrar como pendente. Quem ja esta vinculado (`jaVinculados`) nao e checado
// de novo -- o status dele foi decidido pelo banco no vinculo (0066) e vem em
// `convites`. O aviso e informativo: quem decide o status e o banco, ao salvar.
function PeopleMultiPicker({ value, onChange, exclude, placeholder = "Mencionar pessoas envolvidas…", janela = null, jaVinculados = [], convites = {} }) {
  const store = useStore();
  const [open, setOpen] = useStateMod(false);
  const [q, setQ] = useStateMod("");
  const [agenda, setAgenda] = useStateMod({});
  const ref = React.useRef(null);
  const all = store.users;
  useEffectMod(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  const selected = value || [];
  const iniMs = janela && janela.inicio ? new Date(janela.inicio).getTime() : null;
  const fimMs = janela && janela.fim ? new Date(janela.fim).getTime() : null;
  const novos = selected.filter(id => !jaVinculados.includes(id));
  const chaveNovos = novos.join(",");
  const chaveAgenda = (uid) => `${uid}|${iniMs}|${fimMs}`;
  useEffectMod(() => {
    if (!iniMs || !fimMs || !novos.length || !store.consultarAgenda) return;
    let vivo = true;
    novos.forEach(uid => {
      if (agenda[chaveAgenda(uid)]) return;
      store.consultarAgenda(uid, new Date(iniMs), new Date(fimMs))
        .then(faixas => { if (vivo) setAgenda(a => ({ ...a, [chaveAgenda(uid)]: { faixas: faixas || [] } })); })
        .catch(() => { if (vivo) setAgenda(a => ({ ...a, [chaveAgenda(uid)]: { erro: true } })); });
    });
    return () => { vivo = false; };
  // `agenda` fica fora das dependencias de proposito: incluir faria cada
  // resposta disparar nova rodada. `chaveNovos` ja cobre quem entrou.
  }, [chaveNovos, iniMs, fimMs]);
  const ocupados = novos.filter(uid => (agenda[chaveAgenda(uid)]?.faixas || []).length > 0);
  const semConsulta = novos.filter(uid => agenda[chaveAgenda(uid)]?.erro);
  const nome = (uid) => (all.find(x => x.id === uid)?.nome || "—").split(" ")[0];
  const candidates = all.filter(u => u.id !== exclude && !selected.includes(u.id) && u.nome.toLowerCase().includes(q.trim().toLowerCase()));
  const add = (id) => { onChange([...selected, id]); setQ(""); };
  const remove = (id) => onChange(selected.filter(x => x !== id));
  const ROTULO_STATUS = { pendente: "pendente", aceito: "confirmado", recusado: "recusou" };
  return (
    <div className="people-picker" ref={ref}>
      <div className="pp-chips">
        {selected.map(id => {
          const u = all.find(x => x.id === id);
          if (!u) return null;
          const st = jaVinculados.includes(id) ? convites[id] : null;
          const ocupado = !st && ocupados.includes(id);
          const estilo = (ocupado || st === "pendente")
            ? { background: "var(--color-warn-soft)", color: "var(--color-warn)" }
            : st === "recusado" ? { opacity: 0.6, textDecoration: "line-through" } : undefined;
          return (
            <span key={id} className="pp-chip" style={estilo}
              title={st ? `Convite ${ROTULO_STATUS[st] || st}` : ocupado ? "Agenda ocupada nesse horário: entra como pendente até aceitar" : undefined}>
              <span className="pp-av">{u.initials}</span>{u.nome.split(" ")[0]}
              {st ? <span style={{ fontSize: 10, marginLeft: 3 }}>· {ROTULO_STATUS[st] || st}</span>
                : ocupado ? <span style={{ fontSize: 10, marginLeft: 3 }}>· ocupado</span> : null}
              <button type="button" onClick={() => remove(id)}><Icon name="x" size={9} /></button>
            </span>
          );
        })}
        <input className="pp-input" value={q} placeholder={selected.length ? "" : placeholder} onChange={e => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} />
      </div>
      {ocupados.length > 0 && (
        <div style={{ fontSize: 11.5, color: "var(--color-warn)", marginTop: 6, lineHeight: 1.45 }}>
          {ocupados.map(uid => {
            const f = agenda[chaveAgenda(uid)].faixas[0];
            return <div key={uid}><strong>{nome(uid)}</strong> já tem compromisso das {horaMin(f.inicio)} às {horaMin(f.fim)} · entra como <strong>pendente</strong> e só conta como presente se aceitar.</div>;
          })}
        </div>
      )}
      {semConsulta.length > 0 && (
        <div style={{ fontSize: 11.5, color: "var(--color-ink-soft)", marginTop: 4 }}>
          Não foi possível consultar a agenda de {semConsulta.map(nome).join(", ")} agora. Ao salvar, o sistema confere e decide se o convite fica pendente.
        </div>
      )}
      {open && candidates.length > 0 && (
        <div className="pp-pop">
          {candidates.map(u => (
            <button type="button" key={u.id} className="pp-opt" onClick={() => add(u.id)}>
              <span className="pp-av">{u.initials}</span>
              <span className="pp-nm">{u.nome}</span>
              <span className="pp-papel">{u.papel}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ===== Datepicker + Time Picker =====
// Click on calendar icon -> pick date -> pick time slot. Manual typing blocked.
function InlineDatePicker({ value, onChange, anchorLabel = "Quando", allowPast = false }) {
  const [open, setOpen] = useStateMod(false);
  const [stage, setStage] = useStateMod("date"); // date | time
  const [draft, setDraft] = useStateMod(value || new Date(TODAY.getTime() + 60 * 60 * 1000));
  const ref = React.useRef(null);

  useEffectMod(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const display = value ? value.toLocaleDateString("pt-BR", { day: "2-digit", month: "short" }) + " · " + String(value.getHours()).padStart(2, "0") + ":" + String(value.getMinutes()).padStart(2, "0") : "Selecione data e horário";

  const selectDate = (d) => {
    const next = new Date(draft);
    next.setFullYear(d.getFullYear(), d.getMonth(), d.getDate());
    setDraft(next);
    setStage("time");
  };
  const selectTime = (h, m) => {
    const next = new Date(draft);
    next.setHours(h, m, 0, 0);
    setDraft(next);
    onChange(next);
    setOpen(false);
    setStage("date");
  };

  // Build month grid
  const monthAnchor = new Date(draft);
  const monthStart = new Date(monthAnchor.getFullYear(), monthAnchor.getMonth(), 1);
  const firstDay = new Date(monthStart);
  firstDay.setDate(firstDay.getDate() - firstDay.getDay());
  const cells = Array.from({ length: 42 }, (_, i) => {
    const d = new Date(firstDay); d.setDate(d.getDate() + i); return d;
  });
  const nav = (delta) => {
    const d = new Date(draft); d.setMonth(d.getMonth() + delta); setDraft(d);
  };

  // Time slots
  const slots = [];
  for (let h = 7; h <= 20; h++) { slots.push([h, 0]); slots.push([h, 30]); }

  return (
    <div className="control" style={{ position: "relative", paddingRight: 4 }} ref={ref}>
      <button type="button" className="dp-trigger" onClick={() => { setOpen(o => !o); setStage("date"); }} title="Escolher data e horário">
        <Icon name="calendar" size={14} />
      </button>
      <input readOnly value={display} placeholder={anchorLabel} style={{ flex: 1, cursor: "pointer" }} onClick={() => { setOpen(true); setStage("date"); }} />
      {value && <button type="button" className="reveal-btn" onClick={() => onChange(null)} title="Limpar"><Icon name="x" size={12} /></button>}
      {open && (
        <div className="dp-pop" onClick={e => e.stopPropagation()}>
          <div className="dp-tabs">
            <button className={stage === "date" ? "on" : ""} onClick={() => setStage("date")}>1. Data</button>
            <button className={stage === "time" ? "on" : ""} onClick={() => setStage("time")} disabled={!draft}>2. Horário</button>
          </div>
          {stage === "date" && (
            <div className="dp-date">
              <div className="dp-month-h">
                <button className="icon-btn" onClick={() => nav(-1)}><Icon name="chevron-left" size={14} /></button>
                <div className="dp-month-lbl">{MONTH_NAMES_DP[monthAnchor.getMonth()]} {monthAnchor.getFullYear()}</div>
                <button className="icon-btn" onClick={() => nav(1)}><Icon name="chevron-right" size={14} /></button>
              </div>
              <div className="dp-week-h">
                {["D","S","T","Q","Q","S","S"].map((d, i) => <div key={i}>{d}</div>)}
              </div>
              <div className="dp-grid">
                {cells.map((d, i) => {
                  const inMonth = d.getMonth() === monthAnchor.getMonth();
                  const isToday = d.toDateString() === TODAY.toDateString();
                  const isSel = draft && d.toDateString() === draft.toDateString();
                  const isPast = d < new Date(TODAY.getFullYear(), TODAY.getMonth(), TODAY.getDate());
                  const disabled = isPast && !allowPast;
                  return (
                    <button key={i} disabled={disabled} className={`dp-day ${inMonth ? "" : "out"} ${isToday ? "today" : ""} ${isSel ? "sel" : ""} ${isPast ? "past" : ""}`} onClick={() => !disabled && selectDate(d)}>
                      {d.getDate()}
                    </button>
                  );
                })}
              </div>
              <div className="dp-quick">
                <button onClick={() => selectDate(new Date(TODAY))}>Hoje</button>
                <button onClick={() => { const d = new Date(TODAY); d.setDate(d.getDate() + 1); selectDate(d); }}>Amanhã</button>
                <button onClick={() => { const d = new Date(TODAY); d.setDate(d.getDate() + 7); selectDate(d); }}>+ 7 dias</button>
              </div>
            </div>
          )}
          {stage === "time" && (
            <div className="dp-time">
              <div className="dp-time-h">
                Selecione o horário para <strong>{draft.toLocaleDateString("pt-BR", { weekday: "short", day: "2-digit", month: "short" })}</strong>
              </div>
              <div className="dp-slots">
                {slots.map(([h, m], i) => {
                  const isSel = draft.getHours() === h && draft.getMinutes() === m;
                  return (
                    <button key={i} className={`dp-slot ${isSel ? "sel" : ""}`} onClick={() => selectTime(h, m)}>
                      {String(h).padStart(2, "0")}:{String(m).padStart(2, "0")}
                    </button>
                  );
                })}
              </div>
              <div className="dp-back">
                <button onClick={() => setStage("date")}><Icon name="chevron-left" size={12} /> Voltar para data</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
const MONTH_NAMES_DP = ["Janeiro","Fevereiro","Março","Abril","Maio","Junho","Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"];

// ===== Criar Alerta =====
function NewAlertModal({ leadId, clienteId, lockClient, onClose }) {
  const store = useStore();
  const initialLead = leadId ? store.leads.find(l => l.id === leadId) : null;
  const initialCli = clienteId || initialLead?.clienteId || "";
  const [form, setForm] = useStateMod({
    leadId: leadId || "",
    clienteId: initialCli,
    tipo: "Follow-up",
    when: null,
    canal: "WhatsApp",
    autor: store.me.id,
    envolvidos: [],
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  // Duracao pre-preenchida pelo tipo (uma ligacao nao ocupa o bloco de uma
  // visita tecnica) ate o vendedor mexer nela; dai em diante vale a dele.
  const [duracao, setDuracao] = useStateMod(duracaoDoTipo("Follow-up"));
  const [duracaoTocada, setDuracaoTocada] = useStateMod(false);
  useEffectMod(() => { if (!duracaoTocada) setDuracao(duracaoDoTipo(form.tipo)); }, [form.tipo, duracaoTocada]);
  const fimDe = (inicio) => (inicio ? new Date(new Date(inicio).getTime() + duracao * 60000) : null);

  const isLocked = !!lockClient && !!initialCli;
  const lockedCliente = isLocked ? store.getClienteById(initialCli) : null;

  const submit = async () => {
    if (!form.tipo.trim() || !form.when) {
      store.toast({ kind: "err", msg: "Tipo e data/horário são obrigatórios" });
      return;
    }
    // Espera a gravacao. Fechar antes da resposta jogaria fora o formulario
    // inteiro quando o banco recusa -- e o vendedor veria so um toast de erro,
    // com a tarefa que ele acabou de montar perdida.
    const id = await store.createAlert({ ...form, fim: fimDe(form.when) });
    if (id) onClose();
  };
  return (
    <ModalShell title="Novo alerta" icon="bell" onClose={onClose} width={540} footer={
      <>
        <button className="btn ghost" onClick={onClose}>Cancelar</button>
        <button className="btn primary" onClick={submit}><Icon name="check" size={13} /> Criar alerta</button>
      </>
    }>
      <FormField label="Cliente" required>
        {isLocked ? (
          <div className="cli-chip-field">
            <ClienteAvatar cliente={lockedCliente} size={22} />
            <span style={{ fontWeight: 600, fontSize: 13 }}>{lockedCliente?.nome}</span>
          </div>
        ) : (
          <Typeahead
            value={form.clienteId}
            onChange={v => {
              set("clienteId", v);
              if (v) {
                const l = store.leads.find(x => x.id === form.leadId);
                if (l && l.clienteId !== v) set("leadId", "");
              }
            }}
            icon="search"
            placeholder="Digite para buscar cliente..."
            options={store.clientes.map(c => ({ value: c.id, label: c.nome, sub: c.cidade || "", icon: "user" }))}
          />
        )}
      </FormField>
      {!isLocked && (
        <FormField label="Negócio (opcional)" hint="digite para buscar">
          <Typeahead
            value={form.leadId}
            onChange={v => set("leadId", v)}
            onPickMeta={o => {
              const l = store.leads.find(x => x.id === o.value);
              if (l && l.clienteId && !form.clienteId) set("clienteId", l.clienteId);
            }}
            icon="search"
            placeholder="Digite para buscar negócio..."
            options={store.leads.filter(l => !form.clienteId || l.clienteId === form.clienteId).map(l => ({ value: l.id, label: l.empresa, sub: `${STAGE_NAMES[l.stage]} · ${brlCompact(l.valor)}`, icon: "circle-dot" }))}
          />
        </FormField>
      )}
      <FormField label="Tipo de alerta" required>
        <Select value={form.tipo} onChange={v => set("tipo", v)} options={["Follow-up", "Ligação agendada", "Reunião", "Envio de proposta", "Visita técnica", "Renegociar", "Liberar NF", "Outro"]} />
      </FormField>
      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 10 }}>
        <FormField label="Data e horário" required hint="clique no calendário para escolher">
          <InlineDatePicker value={form.when} onChange={(v) => set("when", v)} />
        </FormField>
        <FormField label="Duração" hint={form.when ? `até ${horaMin(fimDe(form.when))}` : undefined}>
          <Select value={String(duracao)} onChange={v => { setDuracao(Number(v)); setDuracaoTocada(true); }} options={opcoesDuracao(duracao)} />
        </FormField>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <FormField label="Responsável" hint="recebe a tarefa">
          <Select value={form.autor} onChange={v => set("autor", v)} options={store.users.map(u => ({ value: u.id, label: u.nome }))} />
        </FormField>
        <FormField label="Canal">
          <Select value={form.canal} onChange={v => set("canal", v)} options={["WhatsApp", "Telefone", "E-mail", "Google Meet", "Presencial", "Sistema"]} />
        </FormField>
      </div>
      <FormField label="Envolvidos" hint="quem também participa — avisados no sino">
        <PeopleMultiPicker value={form.envolvidos} exclude={form.autor} onChange={v => set("envolvidos", v)}
          janela={form.when ? { inicio: form.when, fim: fimDe(form.when) } : null} />
      </FormField>
      <div style={{ marginTop: 6, padding: 10, background: "var(--color-accent-soft)", borderRadius: "var(--radius-sm)", fontSize: 11.5, color: "var(--color-accent-deep)", display: "flex", alignItems: "flex-start", gap: 8 }}>
        <Icon name="bell" size={13} />
        {/* So o sino: notifyPeople nao envia e-mail. Este texto prometia e-mail
            ate 23/09 -- e um comentario meu de 22/09 afirmava que saia, sem ter
            conferido. Google Calendar tambem nao existe (saiu em 21/09). */}
        <div>Responsável e envolvidos recebem <strong>aviso no sino</strong>. Quem já tem compromisso nesse horário entra como <strong>pendente</strong> e decide se participa.</div>
      </div>
    </ModalShell>
  );
}

// ===== Textarea com @menção (autocomplete de pessoas) =====
// Detecta "@" e mostra um autocomplete flutuante ancorado no cursor.
// Usa position:fixed para não ser cortado pelo modal; insere "@Nome" e registra a menção.
// Calcula a posição do cursor via "mirror div" (espelho do textarea).
function caretXY(ta) {
  const div = document.createElement("div");
  const cs = getComputedStyle(ta);
  ["fontFamily","fontSize","fontWeight","lineHeight","letterSpacing","textTransform","whiteSpace","wordWrap","wordBreak","paddingTop","paddingRight","paddingBottom","paddingLeft","borderTopWidth","borderRightWidth","borderBottomWidth","borderLeftWidth","boxSizing"].forEach(p => { div.style[p] = cs[p]; });
  div.style.position = "absolute";
  div.style.visibility = "hidden";
  div.style.whiteSpace = "pre-wrap";
  div.style.wordWrap = "break-word";
  div.style.width = ta.offsetWidth + "px";
  const pos = ta.selectionStart;
  div.textContent = ta.value.slice(0, pos);
  const span = document.createElement("span");
  span.textContent = ta.value.slice(pos) || ".";
  div.appendChild(span);
  document.body.appendChild(div);
  const rect = ta.getBoundingClientRect();
  const x = rect.left + (span.offsetLeft - ta.scrollLeft);
  const y = rect.top + (span.offsetTop - ta.scrollTop);
  const lh = parseFloat(cs.lineHeight) || (parseFloat(cs.fontSize) * 1.4);
  document.body.removeChild(div);
  return { x, y, lh };
}

function MentionTextarea({ value, onChange, onMentionsChange, placeholder, style, autoFocus }) {
  const store = useStore();
  const taRef = React.useRef(null);
  const [menu, setMenu] = useStateMod(null); // { query, start, items, active, pos:{left,top,placement} }
  const all = store.users;

  const recomputeMentions = (text) => {
    const ids = [];
    all.forEach(u => {
      const re = new RegExp("@" + u.nome.replace(/[.*+?^${}()|[\]\\]/g, "\\$&") + "(?![\\w])", "");
      if (re.test(text)) ids.push({ id: u.id, nome: u.nome });
    });
    onMentionsChange && onMentionsChange(ids);
  };

  const computePos = () => {
    const ta = taRef.current;
    if (!ta) return { left: 0, top: 0, placement: "below" };
    const { x, y, lh } = caretXY(ta);
    const menuH = 230, menuW = 268;
    const spaceBelow = window.innerHeight - (y + lh);
    const placement = spaceBelow < menuH && y > menuH ? "above" : "below";
    let left = Math.min(x, window.innerWidth - menuW - 12);
    left = Math.max(8, left);
    const top = placement === "above" ? y - 6 : y + lh + 4;
    return { left, top, placement };
  };

  const handle = (text, caret) => {
    onChange(text);
    recomputeMentions(text);
    const upto = text.slice(0, caret);
    const m = upto.match(/(^|\s)@([\p{L}]*)$/u);
    if (m) {
      const query = m[2].toLowerCase();
      const start = caret - m[2].length - 1;
      const items = all.filter(u => u.nome.toLowerCase().includes(query));
      setMenu(items.length ? { query, start, items, active: 0, pos: computePos() } : null);
    } else {
      setMenu(null);
    }
  };

  const pick = (u) => {
    const ta = taRef.current;
    const caret = ta.selectionStart;
    const before = value.slice(0, menu.start);
    const after = value.slice(caret);
    const inserted = "@" + u.nome + " ";
    const next = before + inserted + after;
    onChange(next);
    recomputeMentions(next);
    setMenu(null);
    requestAnimationFrame(() => {
      ta.focus();
      const pos = (before + inserted).length;
      ta.setSelectionRange(pos, pos);
    });
  };

  const onKeyDown = (e) => {
    if (!menu) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setMenu(m => ({ ...m, active: (m.active + 1) % m.items.length })); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setMenu(m => ({ ...m, active: (m.active - 1 + m.items.length) % m.items.length })); }
    else if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); pick(menu.items[menu.active]); }
    else if (e.key === "Escape") { e.preventDefault(); setMenu(null); }
  };

  const hl = (nome, query) => {
    if (!query) return nome;
    const i = nome.toLowerCase().indexOf(query);
    if (i < 0) return nome;
    return <>{nome.slice(0, i)}<mark>{nome.slice(i, i + query.length)}</mark>{nome.slice(i + query.length)}</>;
  };

  return (
    <div className="mention-wrap">
      <textarea
        ref={taRef}
        className="textarea"
        autoFocus={autoFocus}
        value={value}
        placeholder={placeholder}
        style={style}
        onChange={e => handle(e.target.value, e.target.selectionStart)}
        onKeyDown={onKeyDown}
        onScroll={() => menu && setMenu(m => m && ({ ...m, pos: computePos() }))}
        onBlur={() => setTimeout(() => setMenu(null), 150)}
      />
      {menu && (
        <div className={`mention-pop ${menu.pos.placement}`} style={{ left: menu.pos.left, top: menu.pos.top, transform: menu.pos.placement === "above" ? "translateY(-100%)" : "none" }}>
          {menu.items.map((u, i) => (
            <button type="button" key={u.id} className={`mention-opt ${i === menu.active ? "active" : ""}`}
              onMouseDown={e => { e.preventDefault(); pick(u); }} onMouseEnter={() => setMenu(m => ({ ...m, active: i }))}>
              <span className="mention-av">{u.initials}</span>
              <span className="mention-nm">{hl(u.nome, menu.query)}</span>
              <span className="mention-papel">{u.papel}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ===== Add Nota (rápido) =====
function AddNoteModal({ leadId, onClose }) {
  const store = useStore();
  const [text, setText] = useStateMod("");
  const [mentions, setMentions] = useStateMod([]);
  const submit = () => {
    if (!text.trim()) return;
    store.addNote(leadId, text.trim(), { mentions });
    window.dispatchEvent(new CustomEvent("beam:note-added", { detail: { leadId } }));
    onClose();
  };
  return (
    <ModalShell title="Adicionar nota" icon="message-square" onClose={onClose} width={480} footer={
      <>
        <button className="btn ghost" onClick={onClose}>Cancelar</button>
        <button className="btn primary" onClick={submit} disabled={!text.trim()} style={{ opacity: text.trim() ? 1 : 0.5 }}>
          <Icon name="check" size={13} /> Adicionar
        </button>
      </>
    }>
      <FormField label="Nota" hint="visível na Timeline">
        <MentionTextarea autoFocus value={text} onChange={setText} onMentionsChange={setMentions} style={{ minHeight: 120 }} placeholder="Ex.: Conversei com o decisor. Demonstrou interesse na automação do funil. Use @ para mencionar um colega." />
      </FormField>
      {mentions.length > 0 && (
        <div className="mention-chips">
          <Icon name="at-sign" size={12} style={{ color: "var(--color-accent-deep)" }} />
          <span className="mc-lbl">Notificar:</span>
          {mentions.map(m => <span key={m.id} className="mention-chip">{m.nome}</span>)}
        </div>
      )}
      <div style={{ fontSize: 11.5, color: "var(--color-ink-soft)" }}>
        <Icon name="info" size={11} style={{ verticalAlign: "-1px", marginRight: 3 }} />
        Digite <code style={{ background: "var(--color-bg)", padding: "1px 4px", borderRadius: 3, fontFamily: "var(--font-mono)", fontSize: 11 }}>@</code> para mencionar um colega (avisado no sino). Use <code style={{ background: "var(--color-bg)", padding: "1px 4px", borderRadius: 3, fontFamily: "var(--font-mono)", fontSize: 11 }}>#urgente</code> para disparar webhooks.
      </div>
    </ModalShell>
  );
}

// ===== Editar Lead =====
// Os campos que o form de edicao pode gravar, lidos do lead como a tela o tem.
// Serve de "antes" para mandar ao banco so o que mudou.
const camposEditaveis = (lead) => ({
  empresa: lead.empresa, contato: lead.contato, cargo: lead.cargo,
  email: lead.email, telefone: lead.telefone, segmento: lead.segmento,
  ownerId: lead.ownerId, proximaAcao: lead.proximaAcao, calor: lead.calor,
  bant: { budget: lead.bant?.budget || "", authority: lead.bant?.authority || "", need: lead.bant?.need || "", timeline: lead.bant?.timeline || "" },
});

function EditLeadModal({ lead, onClose }) {
  const store = useStore();
  // Estágio e dono só são editáveis a quem o banco deixa mudá-los (0087 e 0088).
  const permsLead = usePermissions('pipeline.move_stage', 'pipeline.handoff');
  const escopoLead = useScope();
  const [form, setForm] = useStateMod({
    empresa: lead.empresa, contato: lead.contato, cargo: lead.cargo,
    email: lead.email, telefone: lead.telefone, segmento: lead.segmento,
    valor: lead.valor, stage: lead.stage, ownerId: lead.ownerId,
    proximaAcao: lead.proximaAcao, calor: lead.calor,
    bant_budget: lead.bant?.budget || "", bant_authority: lead.bant?.authority || "",
    bant_need: lead.bant?.need || "", bant_timeline: lead.bant?.timeline || "",
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const submit = async () => {
    if (form.stage !== lead.stage) {
      // moveLeadStage já avisa (sucesso ou falha) e já registra "Estágio movido" na
      // timeline por conta própria. Sem o `await` aqui, "Lead atualizado" e o fecha-
      // mento do modal abaixo aconteciam mesmo quando a RLS recusava a mudança de
      // etapa — a timeline ganhava um "Lead atualizado manualmente" endossando uma
      // troca de estágio que o banco não gravou (achado 2 da revisão final).
      const moveu = await store.moveLeadStage(lead.id, form.stage);
      if (!moveu) return;
    }
    // Roda DEPOIS da guarda acima. Chama-la antes faz os outros campos do form
    // (empresa, owner...) ficarem "salvos" na tela mesmo quando a troca de
    // etapa e recusada pela RLS -- achado 2 da revisao final. Nao reordene.
    //
    // So vai o que MUDOU (reteste do Kaio, 25/09: o e-mail "nao alterava nem
    // sendo admin"). O form mandava tudo, inclusive `valor`, que desde a 0037
    // nao tem grant de UPDATE -- e um campo recusado derruba o UPDATE inteiro
    // (42501). Medido no banco local: o mesmo pacote, trocando so o e-mail,
    // voltava "permission denied for table negocios". Mandar so o alterado
    // tambem evita que `ownerId` intocado esbarre no `with check` de negocios_upd.
    const antes = camposEditaveis(lead);
    const depois = {
      empresa: form.empresa, contato: form.contato, cargo: form.cargo,
      email: form.email, telefone: form.telefone, segmento: form.segmento,
      ownerId: form.ownerId, proximaAcao: form.proximaAcao, calor: form.calor,
      bant: { budget: form.bant_budget, authority: form.bant_authority, need: form.bant_need, timeline: form.bant_timeline },
    };
    const patch = Object.fromEntries(Object.entries(depois)
      .filter(([k, v]) => JSON.stringify(v ?? "") !== JSON.stringify(antes[k] ?? "")));
    if (!Object.keys(patch).length) { onClose(); return; }
    const salvou = await store.updateLead(lead.id, patch);
    // O modal fica ABERTO na recusa, para o usuario corrigir (tirar a troca de
    // responsavel, por exemplo) em vez de perder o que digitou.
    if (!salvou) return;
    store.appendTimeline(lead.id, { tipo: "field", autorTipo: "humano", autor: store.me?.nome, texto: <>Lead atualizado manualmente.</>, when: "agora" });
    store.toast({ kind: "ok", msg: "Lead atualizado" });
    onClose();
  };
  const estagiosDoFunil = STAGES_BY_FUNNEL[lead.funil] || STAGES_PROJETOS;
  const nomeDoEstagio = estagiosDoFunil.find(st => st.id === form.stage)?.name || form.stage;
  // Quem não é gestor só repassa a um closer ativo (0087); o gestor escolhe o dono livremente.
  const opcoesDeDono = escopoLead === 'team'
    ? store.users
    : store.users.filter(u => u.ativo !== false && (u.papel === 'closer' || u.id === form.ownerId));
  const nomeDoDono = store.users.find(u => u.id === form.ownerId)?.nome || '—';
  return (
    <ModalShell title={`Editar · ${lead.empresa}`} icon="edit" onClose={onClose} width={680} footer={
      <>
        <button className="btn ghost" onClick={onClose}>Cancelar</button>
        <button className="btn primary" onClick={submit}><Icon name="save" size={13} /> Salvar</button>
      </>
    }>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <FormField label="Empresa"><Input value={form.empresa} onChange={e => set("empresa", e.target.value)} /></FormField>
        <FormField label="Segmento"><Input value={form.segmento} onChange={e => set("segmento", e.target.value)} /></FormField>
        <FormField label="Contato"><Input icon="user" value={form.contato} onChange={e => set("contato", e.target.value)} /></FormField>
        <FormField label="Cargo"><Input value={form.cargo} onChange={e => set("cargo", e.target.value)} /></FormField>
        <FormField label="E-mail"><Input icon="mail" value={form.email} onChange={e => set("email", e.target.value)} /></FormField>
        <FormField label="Telefone"><PhoneInput value={form.telefone} onChange={v => set("telefone", v)} /></FormField>
        {/* So leitura: desde a 0037 (decisao B, 27/08) o valor e informado na
            criacao e o banco recusa altera-lo depois. Campo editavel aqui
            prometia uma troca que nunca gravava. */}
        <FormField label="Valor" hint="Definido na criação do negócio; não se altera aqui."><Input icon="wallet" value={brl(Number(form.valor) || 0)} disabled readOnly /></FormField>
        <FormField label="Estágio">
          {permsLead['pipeline.move_stage']
            ? <Select value={form.stage} onChange={v => set("stage", v)} options={estagiosDoFunil.map(st => ({ value: st.id, label: st.name }))} />
            : <Input value={nomeDoEstagio} disabled readOnly />}
        </FormField>
        <FormField label="Owner">
          {permsLead['pipeline.handoff']
            ? <Select value={form.ownerId} onChange={v => set("ownerId", v)} options={opcoesDeDono.map(u => ({ value: u.id, label: u.nome }))} />
            : <Input value={nomeDoDono} disabled readOnly />}
        </FormField>
        <FormField label="Calor"><Select value={form.calor} onChange={v => set("calor", v)} options={[{value:"cold",label:"Frio"},{value:"warm",label:"Morno"},{value:"hot",label:"Quente"}]} /></FormField>
      </div>
      <FormField label="Próxima ação"><Input icon="clock" value={form.proximaAcao} onChange={e => set("proximaAcao", e.target.value)} /></FormField>
      <div style={{ marginTop: 8, paddingTop: 12, borderTop: "1px solid var(--color-border)" }}>
        <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--color-ink-soft)", marginBottom: 10 }}>Qualificação BANT</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <FormField label="B · Budget"><Input placeholder="Ex.: R$ 200k aprovado" value={form.bant_budget} onChange={e => set("bant_budget", e.target.value)} /></FormField>
          <FormField label="A · Authority"><Input placeholder="Ex.: Decisor final identificado" value={form.bant_authority} onChange={e => set("bant_authority", e.target.value)} /></FormField>
          <FormField label="N · Need"><Input placeholder="Ex.: Reduzir ciclo de vendas" value={form.bant_need} onChange={e => set("bant_need", e.target.value)} /></FormField>
          <FormField label="T · Timeline"><Input placeholder="Ex.: Q3 2026" value={form.bant_timeline} onChange={e => set("bant_timeline", e.target.value)} /></FormField>
        </div>
      </div>
    </ModalShell>
  );
}

// ===== Novo Webhook (com preview de payload e teste) =====
const TRIGGERS = [
  { value: "lead.created", label: "Lead criado no funil X", icon: "plus" },
  { value: "lead.stage_changed", label: "Lead movido para estágio Y", icon: "git-merge" },
  { value: "lead.field_updated", label: "Campo personalizado atualizado", icon: "edit" },
  { value: "note.hashtag", label: "Nota com hashtag criada (#urgente)", icon: "tag" },
  { value: "lead.won", label: "Lead marcado como GANHO", icon: "check-circle-2" },
  { value: "lead.idle_7d", label: "Lead sem atividade há N dias", icon: "clock" },
];

function NewWebhookModal({ onClose }) {
  const store = useStore();
  const [form, setForm] = useStateMod({
    nome: "", trigger: "lead.created", funil: "Comercial PME",
    url: "https://", headers: [{ k: "Content-Type", v: "application/json" }],
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const payload = useMemoMod(() => makeWebhookPayload(form), [form]);

  const submit = () => {
    if (!form.nome.trim() || !form.url.trim()) {
      store.toast({ kind: "err", msg: "Nome e URL são obrigatórios" });
      return;
    }
    store.createWebhook(form);
    onClose();
  };

  return (
    <ModalShell title="Novo webhook" icon="zap" onClose={onClose} width={780} footer={
      <>
        <button className="btn ghost" onClick={onClose}>Cancelar</button>
        <button className="btn primary" onClick={submit}><Icon name="check" size={13} /> Criar webhook</button>
      </>
    }>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <div>
          <FormField label="Nome" required>
            <Input icon="zap" placeholder="Ex.: Slack #vendas — Novo lead" value={form.nome} onChange={e => set("nome", e.target.value)} autoFocus />
          </FormField>
          <FormField label="Gatilho" required>
            <div style={{ display: "flex", flexDirection: "column", gap: 4, border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)", padding: 4, maxHeight: 180, overflowY: "auto" }}>
              {TRIGGERS.map(t => (
                <button key={t.value} onClick={() => set("trigger", t.value)} style={{
                  display: "flex", alignItems: "center", gap: 8, padding: "8px 10px",
                  background: form.trigger === t.value ? "var(--color-accent-soft)" : "transparent",
                  color: form.trigger === t.value ? "var(--color-accent-deep)" : "var(--color-ink)",
                  border: 0, borderRadius: 6, fontSize: 12.5, fontWeight: form.trigger === t.value ? 600 : 500, textAlign: "left",
                }}>
                  <Icon name={t.icon} size={13} />
                  {t.label}
                </button>
              ))}
            </div>
          </FormField>
          <FormField label="Funil">
            <Select value={form.funil} onChange={v => set("funil", v)} options={["Todos", "Comercial PME", "Enterprise"]} />
          </FormField>
          <FormField label="URL de destino" required>
            <Input icon="link" placeholder="https://api.example.com/webhook" value={form.url} onChange={e => set("url", e.target.value)} />
          </FormField>
          <FormField label="Headers customizados">
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {form.headers.map((h, i) => (
                <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 6 }}>
                  <Input placeholder="Key" value={h.k} onChange={e => set("headers", form.headers.map((x, j) => j === i ? { ...x, k: e.target.value } : x))} />
                  <Input placeholder="Value" value={h.v} onChange={e => set("headers", form.headers.map((x, j) => j === i ? { ...x, v: e.target.value } : x))} />
                  <button className="icon-btn" onClick={() => set("headers", form.headers.filter((_, j) => j !== i))}><Icon name="x" size={14} /></button>
                </div>
              ))}
              <button className="btn sm" style={{ alignSelf: "flex-start" }} onClick={() => set("headers", [...form.headers, { k: "", v: "" }])}>
                <Icon name="plus" size={12} /> Adicionar header
              </button>
            </div>
          </FormField>
        </div>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--color-ink-soft)", marginBottom: 6 }}>Preview do payload</div>
          <pre style={{
            background: "#1B2733", color: "#E2E8F0",
            borderRadius: "var(--radius-sm)", padding: 14, fontSize: 11.5,
            fontFamily: "var(--font-mono)", lineHeight: 1.55,
            overflow: "auto", maxHeight: 380, margin: 0,
          }}>{payload}</pre>
          <div style={{ marginTop: 8, fontSize: 11, color: "var(--color-ink-soft)" }}>
            <Icon name="info" size={11} style={{ verticalAlign: "-1px", marginRight: 3 }} />
            Após criar, use <strong>Testar</strong> na lista para disparar um payload real.
          </div>
        </div>
      </div>
    </ModalShell>
  );
}

// ===== Convidar Usuário =====
// ===== Detalhe API Log =====
function ApiLogDetailModal({ log, onClose }) {
  const reqPayload = log.payloadJson || JSON.stringify({
    empresa: "Vitascale Saúde",
    contato: "Bruno Marques",
    valor_proposta: 0,
    dynamic: { fonte: "AI_AGENT", canal_origem: "WhatsApp", score_llm: 78, fit_comercial: "alto" },
    external_id: "ext_" + Math.random().toString(36).slice(2, 10),
  }, null, 2);
  const respPayload = log.status < 300
    ? JSON.stringify({ id: "ld_" + Math.random().toString(36).slice(2, 8), status: "ok", criado_em: new Date().toISOString() }, null, 2)
    : log.status === 422
      ? JSON.stringify({ error: "validation_failed", details: { email: "formato inválido" } }, null, 2)
      : JSON.stringify({ error: "internal_server_error", trace_id: "trc_" + Math.random().toString(36).slice(2) }, null, 2);

  return (
    <ModalShell title="Detalhe da requisição" icon="terminal" onClose={onClose} width={720} footer={
      <>
        <button className="btn ghost" onClick={onClose}>Fechar</button>
        <button className="btn"><Icon name="copy" size={13} /> Copiar curl</button>
        {log.status >= 400 && <button className="btn primary"><Icon name="refresh" size={13} /> Reprocessar</button>}
      </>
    }>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 12 }}>
        <div><div style={{ fontSize: 10.5, fontWeight: 700, color: "var(--color-ink-soft)", textTransform: "uppercase", letterSpacing: "0.06em" }}>Status</div><StatusHTTP code={log.status} /></div>
        <div><div style={{ fontSize: 10.5, fontWeight: 700, color: "var(--color-ink-soft)", textTransform: "uppercase", letterSpacing: "0.06em" }}>Método</div><span className={`method-pill ${log.method}`}>{log.method}</span></div>
        <div><div style={{ fontSize: 10.5, fontWeight: 700, color: "var(--color-ink-soft)", textTransform: "uppercase", letterSpacing: "0.06em" }}>Latência</div><div style={{ fontFamily: "var(--font-mono)", fontWeight: 700, color: log.time > 1000 ? "var(--color-danger)" : "var(--color-ink)" }}>{log.time}ms</div></div>
        <div><div style={{ fontSize: 10.5, fontWeight: 700, color: "var(--color-ink-soft)", textTransform: "uppercase", letterSpacing: "0.06em" }}>Quando</div><div style={{ fontFamily: "var(--font-mono)", fontSize: 12 }}>{log.ts}</div></div>
      </div>
      <FormField label="Endpoint">
        <div className="control mono"><input readOnly value={log.method + " " + log.endpoint} /></div>
      </FormField>
      <FormField label="Agente">
        <div className="control"><Icon name="cpu" size={13} style={{ color: "var(--color-accent-deep)", marginRight: 6 }} /><input readOnly value={log.agente + " · " + log.token} /></div>
      </FormField>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 8 }}>
        <div>
          <div style={{ fontSize: 10.5, fontWeight: 700, color: "var(--color-ink-soft)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>Request body</div>
          <pre style={{ background: "#1B2733", color: "#E2E8F0", borderRadius: "var(--radius-sm)", padding: 12, fontSize: 11, fontFamily: "var(--font-mono)", margin: 0, maxHeight: 260, overflow: "auto" }}>{reqPayload}</pre>
        </div>
        <div>
          <div style={{ fontSize: 10.5, fontWeight: 700, color: "var(--color-ink-soft)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>Response</div>
          <pre style={{ background: log.status < 300 ? "#0a2a1d" : "#3a1411", color: "#E2E8F0", borderRadius: "var(--radius-sm)", padding: 12, fontSize: 11, fontFamily: "var(--font-mono)", margin: 0, maxHeight: 260, overflow: "auto" }}>{respPayload}</pre>
        </div>
      </div>
    </ModalShell>
  );
}

// ===== Cohort Drill-down =====
// Ate 23/09: "X de 42 leads" com 42 FIXO, e a "amostra desta coorte" eram os 4
// primeiros negocios do sistema, de qualquer semana. Agora mostra os negocios
// DA coorte (detalhe calculado por coorteSemanal), perdidos por ultimo.
function CohortCellModal({ row, col, value, detalhe, onClose }) {
  const store = useStore();
  const ids = new Set((detalhe && detalhe.leadIds) || []);
  const sample = store.leads
    .filter(l => ids.has(l.id))
    .sort((a, b) => Number(!!a.perdido) - Number(!!b.perdido));
  return (
    <ModalShell title={`Coorte ${row} · ${col}`} icon="thermometer" onClose={onClose} width={560} footer={
      <button className="btn ghost" onClick={onClose}>Fechar</button>
    }>
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 14 }}>
        <div style={{ width: 70, height: 70, borderRadius: 12, background: `rgba(14,124,134,${Math.max(0.15, value/100)})`, color: value > 55 ? "white" : "var(--color-ink)", display: "grid", placeItems: "center", fontFamily: "var(--font-mono)", fontWeight: 800, fontSize: 22 }}>{value}%</div>
        <div>
          <div style={{ fontWeight: 700, fontSize: 14 }}>Retenção / conversão</div>
          <div style={{ fontSize: 12, color: "var(--color-ink-soft)" }}>
            {detalhe ? `${detalhe.ativos} de ${detalhe.total} ${detalhe.total === 1 ? "negócio" : "negócios"} ainda ativos ou convertidos` : "—"}
          </div>
        </div>
      </div>
      <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--color-ink-soft)", marginBottom: 8 }}>Negócios desta coorte · situação de hoje</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {sample.map(l => (
          <div key={l.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)" }}>
            <div className="av" style={{ width: 26, height: 26, borderRadius: 6, background: "var(--color-bg)", display: "grid", placeItems: "center", fontSize: 11, fontWeight: 700, color: "var(--color-ink-soft)", border: "1px solid var(--color-border)" }}>{l.empresa.split(" ").map(w => w[0]).slice(0,2).join("")}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 13 }}>{l.empresa}</div>
              <div style={{ fontSize: 11, color: "var(--color-ink-soft)" }}>{l.perdido ? "Perdido" : STAGE_NAMES[l.stage]}{l.contato ? ` · ${l.contato}` : ""}</div>
            </div>
            <OriginPill origin={l.origem} compact />
          </div>
        ))}
      </div>
    </ModalShell>
  );
}

export {
  NewLeadModal, NewAlertModal, AddNoteModal, EditLeadModal,
  NewWebhookModal, ApiLogDetailModal, CohortCellModal,
  NewClienteModal, InlineDatePicker, Typeahead, PeopleMultiPicker,
  EditTaskModal, PdfPreviewModal,
  ModalShell, FormField, Input, Select, PhoneInput, OPCOES_CONTRIBUINTE,
  // Vazamento lexico (Classe 2, task-8/10): nunca foi para window, so consumida por
  // escopo global de script classico (app.jsx). Precisa de export real agora.
  MetaModal,
};

// ===== Editar Tarefa (edição completa de prazo/responsável/descrição/subtarefas) =====
function EditTaskModal({ alertId, onClose }) {
  const store = useStore();
  const alert = store.alerts.find(a => a.id === alertId);
  if (!alert) return null;
  const [form, setForm] = useStateMod({
    tipo: alert.tipo,
    descricao: alert.descricao || "",
    when: alert.when,
    canal: alert.canal,
    autor: alert.autor,
    envolvidos: alert.envolvidos || [],
    prioridade: alert.prioridade || "média",
    // Duracao real da tarefa (fim - inicio), no form e nao num hook novo: este
    // componente ja retorna antes dos hooks quando a tarefa some.
    duracao: (alert.fim && alert.when)
      ? Math.max(1, Math.round((new Date(alert.fim) - new Date(alert.when)) / 60000))
      : duracaoDoTipo(alert.tipo),
  });
  const [newSub, setNewSub] = useStateMod("");
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const cliente = alert.clienteId ? store.getClienteById(alert.clienteId) : null;
  const lead = alert.leadId ? store.leads.find(l => l.id === alert.leadId) : null;

  const submit = async () => {
    if (!form.tipo.trim() || !form.when) {
      store.toast({ kind: "err", msg: "Título e prazo são obrigatórios" });
      return;
    }
    // O fim vai explicito (inicio + duracao): mudar o prazo sem mandar o fim
    // deixaria o fim para tras e o banco recusaria (fim > inicio).
    const { duracao, ...campos } = form;
    const fim = new Date(new Date(form.when).getTime() + duracao * 60000);
    if (await store.updateAlert(alertId, { ...campos, fim }) !== false) onClose();
  };
  const addSub = async () => {
    if (!newSub.trim()) return;
    // So limpa o campo depois que o passo gravou -- senao o texto some junto
    // com a falha.
    if (await store.addSubtask(alertId, newSub.trim()) !== false) setNewSub("");
  };

  return (
    <ModalShell title="Editar tarefa" icon="pencil" onClose={onClose} width={580} footer={
      <>
        <button className="btn ghost" onClick={onClose}>Cancelar</button>
        <button className="btn" onClick={async () => { if (await store.toggleAlertDone(alertId) !== false) onClose(); }}>
          <Icon name={alert.feito ? "refresh" : "check"} size={13} /> {alert.feito ? "Reabrir" : "Concluir"}
        </button>
        <button className="btn primary" onClick={submit}><Icon name="save" size={13} /> Salvar</button>
      </>
    }>
      {(cliente || lead) && (
        <div className="cli-chip-field" style={{ marginBottom: 12 }}>
          {cliente && (<>
            <ClienteAvatar cliente={cliente} size={22} />
            <span style={{ fontWeight: 600, fontSize: 13 }}>{cliente.nome}</span>
          </>)}
          {!cliente && lead && (<>
            <Icon name="circle-dot" size={13} style={{ color: "var(--color-accent)" }} />
            <span style={{ fontWeight: 600, fontSize: 13 }}>{lead.empresa}</span>
          </>)}
        </div>
      )}
      <FormField label="Título" required>
        <Input icon="check-circle-2" value={form.tipo} onChange={e => set("tipo", e.target.value)} />
      </FormField>
      <FormField label="Descrição">
        <textarea className="textarea" value={form.descricao} onChange={e => set("descricao", e.target.value)} placeholder="Detalhes da tarefa…" style={{ minHeight: 70 }} />
      </FormField>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <FormField label="Prazo" required>
          <InlineDatePicker value={form.when} onChange={(v) => set("when", v)} allowPast={true} />
        </FormField>
        <FormField label="Duração" hint={form.when ? `até ${horaMin(new Date(new Date(form.when).getTime() + form.duracao * 60000))}` : undefined}>
          <Select value={String(form.duracao)} onChange={v => set("duracao", Number(v))} options={opcoesDuracao(form.duracao)} />
        </FormField>
        <FormField label="Canal">
          <Select value={form.canal} onChange={v => set("canal", v)} options={["WhatsApp", "Telefone", "E-mail", "Google Meet", "Presencial", "Sistema", "Ligação"]} />
        </FormField>
        <FormField label="Responsável">
          <Select value={form.autor} onChange={v => set("autor", v)} options={store.users.map(u => ({ value: u.id, label: u.nome }))} />
        </FormField>
        <FormField label="Prioridade">
          <div className="prio-pick">
            {["baixa","média","alta"].map(p => (
              <button key={p} type="button" className={`prio-pick-btn prio-${p} ${form.prioridade === p ? "on" : ""}`} onClick={() => set("prioridade", p)}>{p}</button>
            ))}
          </div>
        </FormField>
      </div>
      <FormField label="Envolvidos" hint="quem também participa — avisados no sino">
        <PeopleMultiPicker value={form.envolvidos} exclude={form.autor} onChange={v => set("envolvidos", v)}
          janela={form.when ? { inicio: form.when, fim: new Date(new Date(form.when).getTime() + form.duracao * 60000) } : null}
          jaVinculados={alert.envolvidos || []} convites={alert.convites || {}} />
      </FormField>
      <FormField label="Subtarefas">
        <div className="subtasks-edit">
          {(alert.subtarefas || []).map(s => (
            <label key={s.id} className="subtask-row">
              <input type="checkbox" checked={s.done} onChange={() => store.toggleSubtask(alertId, s.id)} />
              <span style={{ textDecoration: s.done ? "line-through" : "none", color: s.done ? "var(--color-ink-soft)" : "var(--color-ink)" }}>{s.label}</span>
            </label>
          ))}
          <div className="subtask-add">
            <input value={newSub} onChange={e => setNewSub(e.target.value)} onKeyDown={e => e.key === "Enter" && addSub()} placeholder="+ adicionar subtarefa" />
            <button className="btn sm" onClick={addSub} disabled={!newSub.trim()}>Add</button>
          </div>
        </div>
      </FormField>
    </ModalShell>
  );
}

// ===== Meta do mês — acompanhamento de vendas realizadas no período =====
function MetaModal({ onClose }) {
  const store = useStore();
  // Ate 22/09: meta da equipe e metas por funil FIXAS aqui (R$ 1,4 mi /
  // 1 mi / 400 mil), realizado = todos os ganhos de sempre, ritmo em 60%.
  // Agora: meta vigente do banco no mes atual, ganhos por data de ganho.
  const mesId = MONTHS[0].id;
  const metasMes = store.metasDoMes ? store.metasDoMes(mesId) : store.metas;
  const meta = (metasMes && metasMes.empresa) || 0;
  const temMeta = meta > 0;
  const META_MENSAL_POR_FUNIL = (metasMes && metasMes.porFunil) || {};
  const ganhas = useMemoMod(() => store.leads
    .filter(l => ganhoNoMes(l, mesId))
    .sort((a, b) => (b.valor || 0) - (a.valor || 0)), [store.leads, mesId]);
  const realizado = ganhas.reduce((s, l) => s + (l.valor || 0), 0);
  const pct = temMeta ? Math.min(100, Math.round((realizado / meta) * 100)) : 0;
  const falta = Math.max(0, meta - realizado);
  const rt = ritmoDoMes(mesId);
  const porFunil = FUNNELS.map(f => {
    const vendas = ganhas.filter(l => l.funil === f.id);
    const total = vendas.reduce((s, l) => s + (l.valor || 0), 0);
    const metaF = META_MENSAL_POR_FUNIL[f.id] || 0;
    return { ...f, vendas: vendas.length, total, metaF, pctF: metaF ? Math.min(100, Math.round((total / metaF) * 100)) : 0 };
  });

  return (
    <ModalShell title={"Meta do mês · " + MONTHS[0].label} icon="target" onClose={onClose} width={680} footer={
      <button className="btn ghost" onClick={onClose}>Fechar</button>
    }>
      <div className="meta-modal-hero">
        <div className="mm-figures">
          <div>
            <div className="mm-now mono">{brl(realizado)}</div>
            <div className="mm-goal">
              {temMeta
                ? <>realizado de <span className="mono">{brl(meta)}</span> · meta da equipe</>
                : <>sem meta definida para este mês · defina em Gestão › Metas</>}
            </div>
          </div>
          {temMeta && (
            <div className="mm-pct">
              <span className="mono">{pct}%</span>
              <span className="mm-pct-lbl">{pct < rt.ritmo ? "abaixo do ritmo" : "no ritmo"}</span>
            </div>
          )}
        </div>
        <div className="mm-bar">
          <div className="mm-fill" style={{ width: pct + "%" }} />
          {temMeta && (
            <div className="mm-mid" style={{ left: rt.ritmo + "%" }}
              title={`Ritmo para bater a meta: ${rt.ritmo}% até hoje (dia ${rt.dia} de ${rt.diasNoMes})`} />
          )}
        </div>
        <div className="mm-sub">
          <span><Icon name="check-circle-2" size={12} style={{ color: "var(--color-ok)" }} /> <strong>{ganhas.length}</strong> vendas realizadas</span>
          {temMeta && <span><Icon name="wallet" size={12} /> Faltam <strong>{brlCompact(falta)}</strong> para a meta</span>}
        </div>
      </div>

      <div className="mm-funis">
        {porFunil.map(f => (
          <div key={f.id} className="mm-funil">
            <div className="mm-funil-h">
              <Icon name={f.icon} size={13} />
              <span className="nm">{f.name}</span>
              <span className="mono val">{brlCompact(f.total)}</span>
              <span className="mono goal">{f.metaF ? `/ ${brlCompact(f.metaF)}` : "sem meta"}</span>
            </div>
            <div className="mm-funil-bar"><div className="fill" style={{ width: f.pctF + "%" }} /></div>
            <div className="mm-funil-meta">{f.vendas} {f.vendas === 1 ? "venda" : "vendas"}{f.metaF ? ` · ${f.pctF}% da meta` : ""}</div>
          </div>
        ))}
      </div>

      <div className="mm-list-h">
        <Icon name="check-circle-2" size={13} />
        <span>Vendas realizadas no período</span>
        <span className="mono cnt">{ganhas.length}</span>
      </div>
      <div className="mm-list">
        {ganhas.map(l => {
          const owner = store.users.find(u => u.id === l.ownerId) || store.me;
          const tipo = TIPO_CLIENTE_BY_ID[l.tipoCliente];
          return (
            <div key={l.id} className="mm-row">
              <div className="mm-row-av" style={{ background: tipo?.tone, color: tipo?.color }}>{l.empresa.split(" ").map(w => w[0]).slice(0,2).join("")}</div>
              <div className="mm-row-main">
                <div className="nm">{l.empresa}</div>
                <div className="meta">{FUNNELS.find(fn => fn.id === l.funil)?.name} · {tipo?.label || "—"}</div>
              </div>
              <div className="mm-row-owner" title={`Responsável: ${owner.nome}`}><span className="av">{owner.initials}</span>{owner.nome.split(" ")[0]}</div>
              <div className="mm-row-val mono">{brl(l.valor)}</div>
            </div>
          );
        })}
        {ganhas.length === 0 && (
          <div className="empty" style={{ padding: 30 }}>
            <Icon name="target" size={22} style={{ color: "var(--color-ink-faint)", marginBottom: 6 }} />
            <div>Nenhuma venda fechada no período ainda.</div>
          </div>
        )}
      </div>
    </ModalShell>
  );
}

// ===== PDF Preview (modal central) =====
function PdfPreviewModal({ anexo, onClose }) {
  useEffectMod(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  if (!anexo) return null;
  const pages = anexo.pages || 1;
  return (
    <div className="pdf-preview-back" onClick={onClose}>
      <div className="pdf-preview-shell" onClick={e => e.stopPropagation()}>
        <div className="pdf-h">
          <div className="pdf-h-ic" style={{ background: "#C0453B" }}><Icon name="file-text" size={14} /></div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="pdf-h-nm">{anexo.nome}</div>
            <div className="pdf-h-meta">PDF · {anexo.sizeMB} MB · {pages} páginas · enviado por {anexo.uploader} · {anexo.uploadedAt}</div>
          </div>
          <button className="btn sm"><Icon name="download" size={12} /> Baixar</button>
          <button className="icon-btn" onClick={onClose}><Icon name="x" size={16} /></button>
        </div>
        <div className="pdf-body">
          {anexo.blobUrl ? (
            <iframe src={anexo.blobUrl} title={anexo.nome} className="pdf-iframe" />
          ) : (
            <div className="pdf-mock-scroll">
              {Array.from({ length: pages }, (_, i) => (
                <div key={i} className="pdf-page">
                  <div className="pdf-page-h">
                    <span className="mono">{anexo.nome}</span>
                    <span className="mono">pág {i + 1}/{pages}</span>
                  </div>
                  <div className="pdf-page-body">
                    <div className="pl pl-title" style={{ width: "60%" }} />
                    <div className="pl pl-sub" style={{ width: "40%" }} />
                    <div style={{ height: 14 }} />
                    {Array.from({ length: 9 }, (_, j) => (
                      <div key={j} className="pl pl-line" style={{ width: (60 + (j * 7 % 35)) + "%" }} />
                    ))}
                    {i === 0 && anexo.summary && (
                      <div className="pdf-summary">
                        <div className="label">Resumo</div>
                        <div className="txt">{anexo.summary}</div>
                      </div>
                    )}
                    <div style={{ height: 18 }} />
                    {Array.from({ length: 8 }, (_, j) => (
                      <div key={j} className="pl pl-line" style={{ width: (50 + (j * 11 % 40)) + "%" }} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ===== Novo Cliente =====
// Padrao indIEDest da NF-e (clientes.tipo_contribuinte_icms, ck_clientes_tipo_contribuinte).
// Em branco o ERP infere pela IE -- e a inferencia nunca produz o 2.
const OPCOES_CONTRIBUINTE = [
  { value: "", label: "Não informado (segue a IE)" },
  { value: "1", label: "Contribuinte de ICMS" },
  { value: "2", label: "Contribuinte isento" },
  { value: "3", label: "Não contribuinte" },
];

function NewClienteModal({ onClose, defaultTipo, prefill, aoCriar }) {
  const store = useStore();
  const [form, setForm] = useStateMod({
    // `prefill`: o cadastro aberto a partir de um lead (src/cliente-do-lead.js) vem com os dados dele.
    nome: prefill?.nome || "", contato: prefill?.contato || "", cidade: "",
    tipo: defaultTipo || "projeto_direto",
    status: "prospec",
    canalId: "",
    // Sem tabela ate o vendedor escolher. Era "tp_premium", id de uma tabela do
    // prototipo que nem uuid e: cliente criado sem mexer no campo levava esse
    // texto a `clientes.tabela_id` e o banco recusava (22P02).
    tabelaId: "",
    telefone: prefill?.telefone || "", email: prefill?.email || "",
    isCanal: false,
    canalTipo: "Arquitetura",
    parceriaRepasse: true,
    // fiscal + faturamento
    tipoDoc: "cnpj", cnpj: "", cpf: "", ie: "",
    // Razao social e tipo de contribuinte vao ao ERP e decidem o ICMS-ST (decisao
    // do Kaio, 30/09). Em branco, o ERP usa o nome e infere o tipo pela IE.
    razaoSocial: "", tipoContribuinte: "",
    emailFinanceiro: "", condPagamento: "28 dias",
    endereco: { cep: "", rua: "", num: "", complemento: "", bairro: "", cidade: "", uf: "" },
    enderecoEntregaMesmo: true,
    enderecoEntrega: { cep: "", rua: "", num: "", complemento: "", bairro: "", cidade: "", uf: "" },
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const docOk = form.tipoDoc === "cnpj" ? validateCNPJ(form.cnpj) : validateCPF(form.cpf);
  const [erpSync, setErpSync] = useStateMod(true);
  // O ERP e de quem tem `clientes.sync_erp` (ADM de Vendas, Admin, Backoffice). A
  // caixa nascia MARCADA para todo mundo, e a Edge Function nao conferia a
  // permissao: com SDR/BDR cadastrando, eles criariam cliente no ERP sem ela.
  const podeSyncErp = usePermission('clientes.sync_erp');
  const [skipDupCheck, setSkipDupCheck] = useStateMod(false);

  const cnpjLimpo = form.cnpj.replace(/\D/g, "");
  const cpfLimpo  = form.cpf.replace(/\D/g, "");
  const docDup = docOk ? (() => {
    const norm = s => (s || "").replace(/\D/g, "");
    return store.clientes.find(c =>
      form.tipoDoc === "cnpj"
        ? norm(c.cnpj || "") === cnpjLimpo && cnpjLimpo.length === 14
        : norm(c.cpf  || "") === cpfLimpo  && cpfLimpo.length  === 11
    ) || null;
  })() : null;

  const submit = async (force) => {
    if (!form.nome.trim() || !form.contato.trim()) {
      store.toast({ kind: "err", msg: "Nome e contato são obrigatórios" });
      return;
    }
    if (docDup && !force) {
      setSkipDupCheck(true);
      return;
    }
    // `createCliente` passou a ser ASSINCRONO em 18/09: o id e uuid do banco, e
    // so existe depois da escrita. Sem o await, `id` seria uma Promise e o
    // `syncClienteERP` abaixo receberia um objeto no lugar do identificador.
    const id = await store.createCliente({
      ...form,
      canalId: form.canalId || null,
      cnpj: form.tipoDoc === "cnpj" ? form.cnpj : "",
      cpf: form.tipoDoc === "cpf" ? form.cpf : "",
      ie: form.tipoDoc === "cnpj" ? (form.ie || "isento") : "isento",
      razaoSocial: form.razaoSocial.trim(),
      tipoContribuinte: form.tipoContribuinte === "" ? null : Number(form.tipoContribuinte),
      enderecoEntrega: form.enderecoEntregaMesmo ? null : form.enderecoEntrega,
      contatos: [{ id: "ct_" + Date.now().toString(36), nome: form.contato.trim(), cargo: "", telefone: form.telefone, email: form.email, principal: true }],
    });
    // Sem id, a gravacao falhou e o toast de erro ja apareceu. Fechar aqui
    // descartaria tudo o que o vendedor digitou justamente quando ele precisa
    // tentar de novo -- a janela fica aberta com os dados.
    if (!id) return;
    if (erpSync && podeSyncErp && docOk) store.syncClienteERP(id);
    // Aberto a partir de um lead: vincula o cliente novo a ele antes de fechar (o aviso de sucesso e o do
    // vínculo aparecem juntos). Sem `aoCriar` o modal é o de sempre.
    if (aoCriar) await aoCriar(id);
    onClose();
  };
  return (
    <ModalShell title={form.isCanal ? "Novo cliente / canal" : "Novo cliente"} icon="users" onClose={onClose} width={620} footer={
      <>
        <button className="btn ghost" onClick={onClose}>Cancelar</button>
        <button className="btn primary" onClick={() => submit(false)}><Icon name="check" size={13} /> {form.isCanal ? "Criar cliente + canal" : "Criar cliente"}</button>
      </>
    }>
      {prefill && (
        <div style={{ fontSize: 12, color: "var(--color-ink-soft)", marginBottom: 12, lineHeight: 1.45 }}>
          Preenchido com os dados do negócio. Complete o CNPJ/CPF e o endereço para poder fazer a proposta.
        </div>
      )}
      <FormField label="Tipo de cliente" required>
        <div className="tipo-pick">
          {TIPO_CLIENTE.map(t => (
            <button key={t.id} type="button" className={`tipo-pick-btn ${form.tipo === t.id ? "on" : ""}`} style={{ borderColor: form.tipo === t.id ? t.color : undefined, background: form.tipo === t.id ? t.tone : undefined, color: form.tipo === t.id ? t.color : undefined }} onClick={() => set("tipo", t.id)}>
              <span className="dot" style={{ background: t.color }} />
              {t.label}
            </button>
          ))}
        </div>
      </FormField>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <FormField label="Nome" required>
          <Input icon="tag" placeholder="Ex: Residência Itacolomi / Iluminar Studio" value={form.nome} onChange={e => set("nome", e.target.value)} autoFocus />
        </FormField>
        <FormField label="Contato" required>
          <Input icon="user" placeholder="Pessoa responsável" value={form.contato} onChange={e => set("contato", e.target.value)} />
        </FormField>
        <FormField label="Telefone">
          <PhoneInput value={form.telefone} onChange={v => set("telefone", v)} />
        </FormField>
        <FormField label="E-mail">
          <Input icon="mail" placeholder="contato@cliente.com.br" value={form.email}
            onChange={e => set("email", e.target.value)}
            onBlur={e => { const v = e.target.value.trim(); if (v && !form.emailFinanceiro) set("emailFinanceiro", v); }} />
        </FormField>
        <FormField label="Cidade / UF" hint={form.endereco.cidade ? "do endereço de faturamento" : undefined}>
          <Input icon="globe" placeholder="São Paulo · SP" value={form.cidade} onChange={e => set("cidade", e.target.value)} />
        </FormField>
        <FormField label="Status inicial">
          <Select value={form.status} onChange={v => set("status", v)} options={[{value:"prospec",label:"Prospecção"},{value:"ativo",label:"Ativo"},{value:"inativo",label:"Inativo"}]} />
        </FormField>
      </div>
      {form.tipo === "projeto_canal" && (
        <FormField label="Canal / Especificador" required>
          <Select value={form.canalId} onChange={v => set("canalId", v)} options={[{ value: "", label: "Selecione..." }, ...store.especificadores.map(e => ({ value: e.id, label: `${e.nome} · ${e.tipo}` }))]} />
        </FormField>
      )}
      <FormField label="Tabela de preços associada">
        <Select value={form.tabelaId} onChange={v => set("tabelaId", v)} options={(store.tabelasPreco || []).map(t => ({ value: t.id, label: t.nome }))} />
      </FormField>

      {/* Cadastro fiscal + dados de faturamento */}
      <div className="section-h" style={{ marginTop: 14 }}>
        Cadastro fiscal
        {docOk
          ? <span className="doc-valid ok"><Icon name="check" size={10} /> válido</span>
          : <span className="doc-valid err"><Icon name="alert-triangle" size={10} /> obrigatório para gerar propostas</span>}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
        <FormField label="Tipo">
          <Select value={form.tipoDoc} onChange={v => set("tipoDoc", v)} options={[{ value: "cnpj", label: "Pessoa Jurídica" }, { value: "cpf", label: "Pessoa Física" }]} />
        </FormField>
        {form.tipoDoc === "cnpj" ? (
          <FormField label="CNPJ"><Input placeholder="00.000.000/0000-00" value={form.cnpj} onChange={e => { set("cnpj", maskCNPJ(e.target.value)); setSkipDupCheck(false); }} /></FormField>
        ) : (
          <FormField label="CPF"><Input placeholder="000.000.000-00" value={form.cpf} onChange={e => { set("cpf", maskCPF(e.target.value)); setSkipDupCheck(false); }} /></FormField>
        )}
        {form.tipoDoc === "cnpj" && <FormField label="Inscrição Estadual"><Input placeholder="isento" value={form.ie} onChange={e => set("ie", e.target.value)} /></FormField>}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 10 }}>
        <FormField label="Razão social" hint="se diferente do nome fantasia">
          <Input icon="tag" placeholder="Como consta no CNPJ" value={form.razaoSocial} onChange={e => set("razaoSocial", e.target.value)} />
        </FormField>
        <FormField label="Contribuinte de ICMS" hint="define o ICMS-ST">
          <Select value={form.tipoContribuinte} onChange={v => set("tipoContribuinte", v)} options={OPCOES_CONTRIBUINTE} />
        </FormField>
      </div>
      {docDup && skipDupCheck && (
        <div className="doc-dup-warn">
          <Icon name="alert-triangle" size={12} />
          {form.tipoDoc === "cnpj" ? "CNPJ" : "CPF"} já cadastrado em <strong>{docDup.nome}</strong>.
          <button className="btn sm ghost" style={{ marginLeft: 8 }} onClick={() => submit(true)}>Cadastrar mesmo assim</button>
        </div>
      )}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <FormField label="E-mail de faturamento · NF-e">
          <Input icon="mail" placeholder="financeiro@cliente.com.br" value={form.emailFinanceiro} onChange={e => set("emailFinanceiro", e.target.value)} />
        </FormField>
        <FormField label="Condição de pagamento padrão">
          <Select value={form.condPagamento} onChange={v => set("condPagamento", v)} options={["À vista", "28 dias", "30/60", "30/60/90"]} />
        </FormField>
      </div>

      {/* Endereços */}
      <div className="section-h" style={{ marginTop: 14 }}>Endereço de faturamento / cobrança</div>
      <AddrFields value={form.endereco} onChange={v => setForm(f => ({
        ...f, endereco: v,
        // Cidade/UF do cadastro segue o endereço de faturamento enquanto não for editada à mão
        cidade: (v.cidade && v.uf) ? `${v.cidade} · ${v.uf}` : (v.cidade || f.cidade),
      }))} />
      {!form.enderecoEntregaMesmo && (
        <button type="button" className="btn ghost sm" onClick={() => set("endereco", { ...form.enderecoEntrega })}>
          <Icon name="copy" size={12} /> Repetir endereço de entrega na cobrança
        </button>
      )}
      <div className="section-h" style={{ marginTop: 12 }}>Endereço de entrega</div>
      <label className="check-row" style={{ marginBottom: 6 }}>
        <input type="checkbox" checked={form.enderecoEntregaMesmo} onChange={e => set("enderecoEntregaMesmo", e.target.checked)} />
        <span>Entregar no mesmo endereço de faturamento</span>
      </label>
      {!form.enderecoEntregaMesmo && (
        <>
          <AddrFields value={form.enderecoEntrega} onChange={v => set("enderecoEntrega", v)} />
          <button type="button" className="btn ghost sm" onClick={() => set("enderecoEntrega", { ...form.endereco })}>
            <Icon name="copy" size={12} /> Copiar do faturamento
          </button>
        </>
      )}

      {podeSyncErp && <div className="erp-block">
        <div className="erp-h">
          <span className="erp-ic"><Icon name="server" size={13} /></span>
          <div style={{ flex: 1 }}>
            <div className="erp-title">Integração ERP · Nomus EC</div>
            <div className="erp-sub mono">{ERP_CONFIG.baseUrl}/clientes</div>
          </div>
          <span className={`erp-status ${ERP_CONFIG.conectado ? "on" : ""}`}><span className="d" /> {ERP_CONFIG.conectado ? "Conectado" : "Offline"}</span>
        </div>
        <label className="check-row" style={{ marginTop: 8 }}>
          <input type="checkbox" checked={erpSync} onChange={e => setErpSync(e.target.checked)} />
          <span>Criar este cliente também no Nomus ERP ao salvar {!docOk && <em style={{ color: "var(--color-warn)", fontWeight: 600 }}>· requer CNPJ/CPF válido</em>}</span>
        </label>
      </div>}

      <div className={`canal-toggle-block ${form.isCanal ? "on" : ""}`}>
        <label className="check-row canal-toggle-head">
          <input type="checkbox" checked={form.isCanal} onChange={e => set("isCanal", e.target.checked)} />
          <span className="canal-toggle-txt">
            <span className="ct-title"><Icon name="compass" size={13} /> Também é um canal de vendas</span>
            <span className="ct-sub">Arquiteto, designer ou construtora que especifica e traz projetos. Ele continua podendo fazer pedidos como cliente.</span>
          </span>
        </label>
        {form.isCanal && (
          <div className="canal-toggle-fields">
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <FormField label="Tipo de canal">
                <Select value={form.canalTipo} onChange={v => set("canalTipo", v)} options={["Arquitetura", "Designer", "Construtora", "Marcenaria", "Representante", "Showroom", "Lojista", "Outro"].map(t => ({ value: t, label: t }))} />
              </FormField>
              <FormField label="Responsável (especificador)">
                <Input icon="user" placeholder="Quem especifica" value={form.canalResponsavel ?? form.contato} onChange={e => set("canalResponsavel", e.target.value)} />
              </FormField>
            </div>
            <label className="check-row" style={{ marginTop: 4 }}>
              <input type="checkbox" checked={form.parceriaRepasse} onChange={e => set("parceriaRepasse", e.target.checked)} />
              <span>Parceria com repasse de comissão?</span>
            </label>
            {/* Nao ha campo de %: `clientes.pct_repasse` e so de service_role/admin
                desde a 0038 (a % desce para o negocio pelo gatilho da 0037), e um
                campo que nao grava e a mesma fachada de "salvo na tela, some no
                F5". Achado do reteste de 30/09. */}
            {form.parceriaRepasse && (
              <div style={{ fontSize: 12, color: "var(--color-ink-soft)", marginTop: 4, lineHeight: 1.5 }}>
                A % do repasse é definida pelo Financeiro depois do cadastro. Até lá o canal fica sem %,
                e o repasse dos negócios dele sai zerado.
              </div>
            )}
          </div>
        )}
      </div>
    </ModalShell>
  );
}
